import React from 'react';
import { TableRow, TableCell, Box, Typography, Chip } from '@mui/material';
import { LandingPage, User } from '../../../types';
import { SiteService } from '../../../services/siteService';
import { ActionButton } from './ActionButton';
import {
  ModeEdit as EditIcon,
  BarChart as AnalyticsIcon,
  MoreHoriz as MoreVertIcon,
} from '@mui/icons-material';
import { formatDate, formatDateTime } from '../../../utils/dateFormatter';
import { getDisplayUrl } from '../../../utils/urlHelper';
import { getStatusColor, getStatusLabel, isStatusPublished } from '../../../utils/statusHelper';
import type { SiteTableRowProps } from '../../../types/admin';

export const SiteTableRow: React.FC<SiteTableRowProps> = ({
  site,
  domainConfig,
  activeDomain,
  usersCache,
  currentUser,
  onEdit,
  onAnalytics,
  onMenuOpen,
  showEditButton = true,
}) => {
  const actualStatus = SiteService.getActualStatusSync(site, domainConfig);
  const statusLabel = getStatusLabel(actualStatus);
  const isPublished = isStatusPublished(actualStatus);
  const statusColor = getStatusColor(actualStatus);
  const publishedDate = isPublished ? formatDate(site.updatedAt) : '-';
  const updatedAt = site.updatedAt || site.createdAt;
  const updatedAtStr = updatedAt ? formatDateTime(updatedAt) : '-';
  const basePath = domainConfig?.basePath || '';
  const url = activeDomain ? getDisplayUrl(activeDomain, basePath) : null;
  const updaterUserId = site.updatedByUserId || site.createdByUserId;
  const updaterUser = updaterUserId ? usersCache[updaterUserId] : null;
  const updaterName = updaterUser 
    ? (updaterUser.displayName || updaterUser.email || '-')
    : (currentUser && !updaterUserId ? (currentUser.displayName || currentUser.email) : '-');
  const companyName = site.companyUserId && usersCache[site.companyUserId]
    ? (usersCache[site.companyUserId].displayName || '-')
    : '-';
  const thumbnailImage = site.pages && site.pages.length > 0 && site.pages[0].backgroundImage 
    ? site.pages[0].backgroundImage 
    : null;
  // サイト名列: タイトルタグが未設定のときのみタブに表示される「タイトル」＝サイト名。設定済みのときもサイト名を表示する。
  const siteNameDisplay = site.title || '';

  return (
    <TableRow sx={{ 
      '&:hover': { 
        backgroundColor: '#F5F5F5',
        '& .action-cell': {
          backgroundColor: '#F5F5F5',
        },
      }, 
      borderBottom: 'none' 
    }}>
      <TableCell align="center" sx={{ borderBottom: 'none', py: 1.5, pl: 3, width: 100, minWidth: 100 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {thumbnailImage ? (
            <Box
              component="img"
              src={thumbnailImage}
              alt={site.title}
              sx={{
                width: 'auto',
                height: '50px',
                maxWidth: '70px',
                display: 'block',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '70px',
                height: '50px',
                backgroundColor: '#F5F5F5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ color: '#9E9E9E', fontSize: '10px' }}>No Image</Typography>
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ color: '#000000', fontSize: '16px', fontWeight: 500, borderBottom: 'none', py: 1.5 }}>
        {siteNameDisplay}
      </TableCell>
      <TableCell sx={{ borderBottom: 'none', py: 1.5 }}>
        {url ? (
          <Typography
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#1B76D2',
              textDecoration: 'none',
              fontSize: '14px',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {url}
          </Typography>
        ) : (
          <Typography sx={{ color: '#000000', fontSize: '14px' }}>-</Typography>
        )}
      </TableCell>
      <TableCell sx={{ borderBottom: 'none', py: 1.5 }}>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            backgroundColor: statusColor,
            borderRadius: '10px',
            color: isPublished ? '#FFFFFF' : 'rgba(0, 0, 0, 0.6)',
            fontSize: '12px',
            fontWeight: 300,
            height: '26px',
            border: isPublished ? 'none' : '1px solid #E0E0E0',
          }}
        />
      </TableCell>
      <TableCell sx={{ color: '#000000', fontSize: '14px', borderBottom: 'none', py: 1.5 }}>
        {publishedDate}
      </TableCell>
      <TableCell sx={{ color: '#000000', fontSize: '14px', borderBottom: 'none', py: 1.5 }}>
        {companyName}
      </TableCell>
      <TableCell sx={{ color: '#000000', fontSize: '14px', borderBottom: 'none', py: 1.5 }}>
        {updaterName}
      </TableCell>
      <TableCell sx={{ color: '#000000', fontSize: '14px', borderBottom: 'none', py: 1.5 }}>
        {updatedAtStr}
      </TableCell>
      <TableCell 
        align="center" 
        className="action-cell"
        sx={{ borderBottom: 'none', py: 1.5, position: 'sticky', right: 0, backgroundColor: '#FFFFFF', zIndex: 10, pr: 2 }}
      >
        <Box display="flex" gap={1} justifyContent="center" alignItems="center">
          {showEditButton ? (
            <>
              {onEdit && (
                <ActionButton
                  onClick={() => onEdit(site)}
                  icon={<EditIcon sx={{ fontSize: '18px' }} />}
                />
              )}
              {onAnalytics && (
                <ActionButton
                  onClick={() => onAnalytics(site)}
                  icon={<AnalyticsIcon sx={{ fontSize: '18px' }} />}
                  backgroundColor="#FF9800"
                  color="#FFFFFF"
                  borderColor="#FF9800"
                  hoverBackgroundColor="#F57C00"
                />
              )}
              {onMenuOpen && (
                <ActionButton
                  onClick={(e) => onMenuOpen(e, site)}
                  icon={<MoreVertIcon sx={{ fontSize: '18px' }} />}
                  backgroundColor="#616161"
                  color="#FFFFFF"
                  borderColor="#616161"
                  hoverBackgroundColor="#424242"
                />
              )}
            </>
          ) : (
            onAnalytics && (
              <ActionButton
                onClick={() => onAnalytics(site)}
                icon={<AnalyticsIcon sx={{ fontSize: '18px' }} />}
                backgroundColor="#FF9800"
                color="#FFFFFF"
                borderColor="#FF9800"
                hoverBackgroundColor="#F57C00"
              />
            )
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

